import { FilterUpdateEvent } from '@theme/events';

/**
 * Collection toolbar, filter drawer, sort, active-filter chips and "Load more".
 *
 * The whole section (header, toolbar, chips, grid, drawer) lives inside
 * <results-list id="product-grid-container">. Filtering re-renders it through the
 * Section Rendering API and swaps its innerHTML, so every listener is delegated
 * from that persistent element and nothing needs re-binding.
 */

const SELECTORS = {
  root: '[data-laa-collection]',
  filterOpen: '[data-laa-filter-open]',
  filterClose: '[data-laa-filter-close]',
  drawer: '[data-laa-filter-drawer]',
  form: '[data-laa-filter-form]',
  clear: '[data-laa-filter-clear]',
  apply: '[data-laa-filter-apply]',
  sort: '[data-laa-sort]',
  ajaxLink: 'a[data-laa-ajax]',
  loadMore: '[data-laa-load-more]',
  count: '[data-laa-product-count]',
  grid: '.product-grid',
  card: '[ref="cards[]"]',
};

const HISTORY_KEY = 'laaCollection';
const COUNT_DEBOUNCE_MS = 300;

class LaaCollection {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || root.getAttribute('section-id') || '';
    this.events = new AbortController();
    /** @type {AbortController | null} */
    this.renderRequest = null;
    /** @type {AbortController | null} */
    this.countRequest = null;
    /** @type {number | undefined} */
    this.countTimer = undefined;

    this.liveRegion = this.#createLiveRegion();
    this.#bind();
    this.#disableInfiniteNextPage();

    history.replaceState({ ...(history.state ?? {}), [HISTORY_KEY]: true }, '', window.location.href);
  }

  destroy() {
    this.events.abort();
    this.renderRequest?.abort();
    this.countRequest?.abort();
    this.liveRegion.remove();
  }

  #bind() {
    const { signal } = this.events;

    this.root.addEventListener('click', this.#onClick, { signal });
    this.root.addEventListener('submit', this.#onSubmit, { signal });
    this.root.addEventListener('change', this.#onChange, { signal });
    this.root.addEventListener('input', this.#onInput, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });
  }

  /**
   * "Load more" replaces automatic loading of the next page. The previous-page
   * observer (used when landing on ?page=N) is left alone.
   */
  async #disableInfiniteNextPage() {
    await customElements.whenDefined('results-list');
    const viewMoreNext = this.root.querySelector('[ref="viewMoreNext"]');
    // `infinityScrollObserver` is a public field on PaginatedList.
    const observer = /** @type {any} */ (this.root).infinityScrollObserver;
    if (observer instanceof IntersectionObserver && viewMoreNext) observer.unobserve(viewMoreNext);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest(SELECTORS.filterOpen)) {
      this.#openDrawer();
      return;
    }

    if (target.closest(SELECTORS.filterClose)) {
      this.#closeDrawer();
      return;
    }

    const drawer = this.#drawer();
    // Click on the ::backdrop lands on the dialog element itself.
    if (drawer && target === drawer) {
      this.#closeDrawer();
      return;
    }

    const clear = target.closest(SELECTORS.clear);
    if (clear instanceof HTMLElement) {
      event.preventDefault();
      this.#closeDrawer();
      this.render(clear.dataset.url || window.location.pathname, { focusKey: 'filter-open' });
      return;
    }

    const loadMore = target.closest(SELECTORS.loadMore);
    if (loadMore instanceof HTMLAnchorElement) {
      if (this.#isModifiedClick(event)) return;
      event.preventDefault();
      this.#loadMore(loadMore);
      return;
    }

    const link = target.closest(SELECTORS.ajaxLink);
    if (link instanceof HTMLAnchorElement) {
      if (this.#isModifiedClick(event)) return;
      event.preventDefault();
      this.render(link.href);
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(SELECTORS.form)) return;

    event.preventDefault();
    this.#closeDrawer();
    this.render(this.#urlFromForm(form), { focusKey: 'filter-open' });
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.matches(SELECTORS.sort)) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      this.render(url.toString());
      return;
    }

    if (target instanceof HTMLInputElement && target.form?.matches(SELECTORS.form)) {
      this.#scheduleCount(target.form);
    }
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'number' && target.form?.matches(SELECTORS.form)) {
      this.#scheduleCount(target.form);
    }
  };

  /** @param {PopStateEvent} event */
  #onPopState = (event) => {
    if (!event.state?.[HISTORY_KEY]) return;
    this.render(window.location.href, { push: false });
  };

  /** @param {MouseEvent} event */
  #isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
  }

  /** @returns {HTMLDialogElement | null} */
  #drawer() {
    const drawer = this.root.querySelector(SELECTORS.drawer);
    return drawer instanceof HTMLDialogElement ? drawer : null;
  }

  #openDrawer() {
    const drawer = this.#drawer();
    if (!drawer || drawer.open) return;

    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    drawer.showModal();
    document.documentElement.classList.add('laa-scroll-lock');
    drawer.addEventListener('close', this.#onDrawerClose, { once: true, signal: this.events.signal });
  }

  #closeDrawer() {
    const drawer = this.#drawer();
    if (drawer?.open) drawer.close();
  }

  #onDrawerClose = () => {
    document.documentElement.classList.remove('laa-scroll-lock');
    clearTimeout(this.countTimer);
    this.countRequest?.abort();
    if (this.opener?.isConnected) this.opener.focus();
  };

  /**
   * Builds the collection URL from the drawer form, keeping params the form
   * does not own (e.g. sort_by on desktop, tracking params) and dropping page.
   * @param {HTMLFormElement} form
   */
  #urlFromForm(form) {
    const url = new URL(window.location.href);
    const formData = new FormData(form);
    const owned = new Set(
      [...form.elements]
        .map((element) => (element instanceof HTMLInputElement || element instanceof HTMLSelectElement ? element.name : ''))
        .filter(Boolean)
    );

    for (const name of owned) url.searchParams.delete(name);
    url.searchParams.delete('page');

    for (const [name, value] of formData.entries()) {
      if (typeof value !== 'string' || value.trim() === '') continue;
      url.searchParams.append(name, value);
    }

    return url.toString();
  }

  /** @param {string | URL} url */
  #sectionUrl(url) {
    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    return fetchUrl;
  }

  /** @param {string | URL} url @param {AbortSignal} signal */
  async #fetchSection(url, signal) {
    const response = await fetch(this.#sectionUrl(url), { signal });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * Re-renders the whole section for `url`.
   * @param {string} url
   * @param {{ push?: boolean, focusKey?: string }} [options]
   */
  async render(url, { push = true, focusKey: requestedFocusKey } = {}) {
    this.renderRequest?.abort();
    const request = new AbortController();
    this.renderRequest = request;

    const focusKey =
      requestedFocusKey ??
      (document.activeElement instanceof HTMLElement ? document.activeElement.dataset.laaFocusKey : undefined);
    this.root.setAttribute('aria-busy', 'true');
    this.root.classList.add('is-loading');

    try {
      const html = await this.#fetchSection(url, request.signal);
      const next = html.getElementById(this.root.id);
      if (!next) throw new Error('Section markup not found');

      const cleanUrl = new URL(url, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      if (push) history.pushState({ [HISTORY_KEY]: true }, '', cleanUrl.toString());

      this.root.innerHTML = next.innerHTML;
      this.#disableInfiniteNextPage();
      document.dispatchEvent(new FilterUpdateEvent(cleanUrl.searchParams));

      this.#restoreFocus(focusKey);
      this.#announce();
    } catch (error) {
      if (request.signal.aborted) return;
      console.error(error);
      window.location.assign(url);
    } finally {
      if (this.renderRequest === request) {
        this.root.removeAttribute('aria-busy');
        this.root.classList.remove('is-loading');
        this.renderRequest = null;
      }
    }
  }

  /** @param {string | undefined} focusKey */
  #restoreFocus(focusKey) {
    const target =
      (focusKey && this.root.querySelector(`[data-laa-focus-key="${CSS.escape(focusKey)}"]`)) ||
      this.root.querySelector(SELECTORS.count);
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  #announce() {
    const count = this.root.querySelector(SELECTORS.count);
    if (count) this.liveRegion.textContent = count.textContent?.trim() ?? '';
  }

  /** @param {HTMLAnchorElement} button */
  async #loadMore(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const nextPage = Number(button.dataset.nextPage);
    const lastPage = Number(button.dataset.lastPage);
    const grid = this.root.querySelector(SELECTORS.grid);
    if (!grid || !Number.isFinite(nextPage)) {
      window.location.assign(button.href);
      return;
    }

    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('page', String(nextPage));
    pageUrl.hash = '';

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    try {
      const html = await this.#fetchSection(pageUrl, this.events.signal);
      const cards = [...html.querySelectorAll(`${SELECTORS.grid} ${SELECTORS.card}`)];
      if (!cards.length) throw new Error('No products in next page');

      grid.append(...cards);
      history.replaceState({ [HISTORY_KEY]: true }, '', pageUrl.toString());

      const firstLink = cards[0]?.querySelector('a[href]');
      if (firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: false });

      if (nextPage >= lastPage) {
        button.closest('.laa-collection__pagination')?.remove();
      } else {
        button.dataset.nextPage = String(nextPage + 1);
        const nextUrl = new URL(pageUrl);
        nextUrl.searchParams.set('page', String(nextPage + 1));
        button.href = nextUrl.toString();
      }
    } catch (error) {
      if (this.events.signal.aborted) return;
      console.error(error);
      window.location.assign(button.href);
    } finally {
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-loading');
    }
  }

  /** @param {HTMLFormElement} form */
  #scheduleCount(form) {
    clearTimeout(this.countTimer);
    this.countTimer = window.setTimeout(() => this.#updateCount(form), COUNT_DEBOUNCE_MS);
  }

  /**
   * Fetches the pending selection to show an accurate "Show N results".
   * @param {HTMLFormElement} form
   */
  async #updateCount(form) {
    const apply = form.querySelector(SELECTORS.apply);
    if (!(apply instanceof HTMLElement)) return;

    this.countRequest?.abort();
    const request = new AbortController();
    this.countRequest = request;

    try {
      const html = await this.#fetchSection(this.#urlFromForm(form), request.signal);
      const count = Number(html.querySelector(SELECTORS.count)?.getAttribute('data-count'));
      if (!Number.isFinite(count)) return;

      const template = count === 1 ? apply.dataset.labelOne : apply.dataset.labelOther;
      if (!template) return;
      apply.textContent = count === 1 ? template : template.replace('999999', count.toLocaleString());
    } catch (error) {
      if (!request.signal.aborted) console.error(error);
    }
  }

  #createLiveRegion() {
    const region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.append(region);
    return region;
  }
}

/** @type {LaaCollection | null} */
let instance = null;

const init = () => {
  const root = document.querySelector(SELECTORS.root);
  if (!(root instanceof HTMLElement) || root.dataset.laaReady) return;

  instance?.destroy();
  root.dataset.laaReady = 'true';
  instance = new LaaCollection(root);
};

// Theme editor re-renders the section element itself.
document.addEventListener('shopify:section:load', init);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
